import { useCallback, useEffect, useRef, useState } from 'react';
import { Heading, Text, Button, Select, Spinner, Callout } from 'frosted-ui';
import { ArrowUpRightFromSquare16, ChevronDown16, ChevronUp16, Rotate16, InfoCircle16 } from '@frosted-ui/icons';
import type { ProductPlan } from '../../types';
import { useCompanyId } from '../../contexts/CompanyContext';
import { useProductCatalog, isFreeProduct, type ProductWithPlans } from '../../hooks/useProductCatalog';
import { priceTestForPlan } from '../../utils/suggestions';
import { getMinimumPrice } from '../../utils/currency';
import { describeOffer, describePlan } from '../../utils/variants';
import LoadFailed from '../LoadFailed';
import VariantsEditor, { PriceField } from './VariantsEditor';
import { controlIsToday, type WizardData } from './wizardTypes';

const INTERVAL_WORD: Record<ProductPlan['interval'], string> = {
  week: 'weekly',
  month: 'monthly',
  year: 'yearly',
  one_time: 'one-time',
};

function autoName(productName: string, plan: ProductPlan | undefined): string {
  return `${productName} — ${plan ? INTERVAL_WORD[plan.interval] : 'monthly'} price`;
}

// A product with no public price yet: the seller sets both, so start from
// something editable rather than whatever the last product left behind.
const NO_PLAN_TEST: Pick<WizardData, 'variants' | 'split'> = {
  variants: [
    { name: 'A', price: 999, interval: 'month', trialDays: 0 },
    { name: 'B', price: 1299, interval: 'month', trialDays: 0 },
  ],
  split: { A: 50, B: 50 },
};

interface StepWhatToTestProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
  /** Whop's in-app checkout can't apply a promo code, so the in-whop path has none. */
  promosAllowed: boolean;
  /** Start from this product ("Run another test"). */
  initialProductId?: string | null;
  /** Whether the chosen product's prices actually loaded. */
  onCatalogReady: (ready: boolean) => void;
}

const paidPlans = (entry: ProductWithPlans) => entry.plans.filter((p) => p.price > 0);

/**
 * Whether a test can sell this plan exactly as buyers see it today. If not (a
 * first charge, a period other than week, month or year, or a price under the
 * minimum) Control stays on show rather than quietly being something else.
 */
const testCanCopy = (plan: ProductPlan) =>
  !plan.firstCharge && !plan.billingPeriodDays && plan.price >= getMinimumPrice(plan.currency);

export default function StepWhatToTest({
  data,
  onChange,
  promosAllowed,
  initialProductId,
  onCatalogReady,
}: StepWhatToTestProps) {
  const companyId = useCompanyId();
  const { catalog, loading, error, reload } = useProductCatalog(companyId);
  const nameTouched = useRef(false);
  // The seller typed prices: a late-arriving plan must not overwrite them.
  const pricesEdited = useRef(false);
  // Open already when coming Back to something set in here.
  const [moreOpen, setMoreOpen] = useState(
    data.variants.length > 2 ||
      data.variants.some((v) => v.promoCodeId || v.interval !== 'month')
  );

  // A free product has no price to test.
  const products = catalog.filter((e) => !isFreeProduct(e));

  const applyPlan = useCallback(
    (entry: ProductWithPlans, plan: ProductPlan | undefined) => {
      const priceTest = plan ? priceTestForPlan(plan) : null;
      onChange({
        productId: entry.product.id,
        productName: entry.product.name,
        basePlanId: plan?.id ?? '',
        baseOffer:
          plan && testCanCopy(plan)
            ? { price: plan.price, interval: plan.interval, trialDays: plan.trialPeriodDays }
            : undefined,
        ...(plan && priceTest
          ? { currency: plan.currency, variants: priceTest.variants, split: priceTest.split }
          : NO_PLAN_TEST),
        ...(nameTouched.current ? {} : { name: autoName(entry.product.name, plan) }),
      });
    },
    [onChange]
  );

  // Once products load, prefill from the asked-for product, else the first with
  // a paid public price, so the Control is the seller's real price.
  const needsStarter = !data.productId;
  useEffect(() => {
    if (!needsStarter || products.length === 0) return;
    const starter =
      products.find((e) => e.product.id === initialProductId) ??
      products.find((e) => paidPlans(e).length > 0) ??
      products[0];
    applyPlan(starter, paidPlans(starter)[0]);
    // `products` is rebuilt each render; the catalog is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsStarter, catalog, initialProductId, applyPlan]);

  const entry = products.find((e) => e.product.id === data.productId);
  const plans = entry ? paidPlans(entry) : [];
  const selectedPlan = plans.find((p) => p.id === data.basePlanId);
  const basePlan = selectedPlan && testCanCopy(selectedPlan) ? selectedPlan : undefined;
  // The product has a price we haven't applied: its plans failed to load before
  // (a retry just brought them), or the seller added one and pressed Refresh.
  const planToApply =
    entry && !entry.plansFailed && !data.basePlanId && !pricesEdited.current ? plans[0] : undefined;
  useEffect(() => {
    if (entry && planToApply) applyPlan(entry, planToApply);
    // `entry` is rebuilt each render; the plan is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planToApply?.id, applyPlan]);
  const ready = Boolean(entry && !entry.plansFailed && !planToApply);
  useEffect(() => onCatalogReady(ready), [ready, onCatalogReady]);

  function selectProduct(productId: string) {
    pricesEdited.current = false;
    const next = products.find((e) => e.product.id === productId);
    if (next) applyPlan(next, paidPlans(next)[0]);
  }

  function selectPlan(planId: string) {
    pricesEdited.current = false;
    if (entry) applyPlan(entry, plans.find((p) => p.id === planId));
  }

  function setPrice(index: number, price: number) {
    pricesEdited.current = true;
    onChange({ variants: data.variants.map((v, i) => (i === index ? { ...v, price } : v)) });
  }

  const control = data.variants[0];
  const challenger = data.variants[1];
  const lift =
    control && challenger && control.price > 0
      ? Math.round(((challenger.price - control.price) / control.price) * 100)
      : null;

  return (
    <div className="vara2-wizard-stack" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div>
        <Heading size="5" style={{ marginBottom: 'var(--space-1)' }}>
          What do you want to test?
        </Heading>
        <Text size="2" color="gray">
          Half your buyers see today's price (the Control), half see a new one (the Challenger).
          Your store page doesn't change.
        </Text>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
          <Text as="label" size="2" weight="medium">
            Product
          </Text>
          <Button type="button" variant="ghost" size="1" color="gray" onClick={reload} disabled={loading}>
            <Rotate16 />
            Refresh
          </Button>
        </div>

        {error && (
          <div style={{ marginBottom: 'var(--space-2)' }}>
            <LoadFailed what="your products" onRetry={reload} />
          </div>
        )}

        {loading && catalog.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3)' }}>
            <Spinner size="2" />
            <Text size="2" color="gray">Loading your products…</Text>
          </div>
        ) : products.length > 0 ? (
          <Select.Root value={data.productId} onValueChange={selectProduct} size="2">
            <Select.Trigger placeholder="Pick a product…" variant="surface" style={{ width: '100%' }} />
            <Select.Content>
              {products.map((e) => (
                <Select.Item key={e.product.id} value={e.product.id}>
                  {e.product.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : error ? null : (
          <Callout.Root color="gray">
            <Callout.Icon><InfoCircle16 /></Callout.Icon>
            <Callout.Text>
              {catalog.length > 0
                ? "Your products are all free, so there's no price to test yet. Add a paid product in Whop, then press Refresh."
                : 'No products yet. Create one in Whop, then press Refresh.'}{' '}
              <a href={`https://whop.com/dashboard/${companyId}/`} target="_blank" rel="noopener noreferrer">
                Open Whop dashboard <ArrowUpRightFromSquare16 style={{ verticalAlign: 'text-bottom' }} />
              </a>
            </Callout.Text>
          </Callout.Root>
        )}

        {plans.length > 1 && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              Which price do buyers pay today?
            </Text>
            <Select.Root value={data.basePlanId} onValueChange={selectPlan} size="2">
              <Select.Trigger variant="surface" style={{ width: '100%' }} />
              <Select.Content>
                {plans.map((p) => (
                  <Select.Item key={p.id} value={p.id}>
                    {describePlan(p)}
                    {p.name ? ` — ${p.name}` : ''}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
        )}

        {entry && !entry.plansFailed && selectedPlan && !basePlan ? (
          <Text size="2" color="gray" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
            Buyers pay {describePlan(selectedPlan)} today. A test can't copy that, so set the prices
            and billing you want to compare (billing is under More options).
          </Text>
        ) : entry && !entry.plansFailed && plans.length <= 1 ? (
          <Text size="2" color="gray" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
            {basePlan
              ? `Buyers pay ${describeOffer({ ...basePlan, trialDays: basePlan.trialPeriodDays })} today.${controlIsToday(data) ? " That's your Control." : ''}`
              : 'This product has no public price yet, so set both prices.'}
          </Text>
        ) : null}
        {entry?.plansFailed && (
          <Text size="2" color="gray" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
            We need this product's prices before you can set up the test.
          </Text>
        )}
      </div>

      {entry && !entry.plansFailed && control && challenger && (
        <>
          <div className="variant-fields-grid" style={{ display: 'grid', gridTemplateColumns: basePlan ? '1fr' : '1fr 1fr', gap: 'var(--space-4)' }}>
            {!basePlan && (
              <PriceField
                label="Control price"
                variant={control}
                currency={data.currency}
                onChange={(price) => setPrice(0, price)}
              />
            )}
            <PriceField
              label="Challenger price"
              hint={
                basePlan && lift !== null && lift !== 0
                  ? `${lift > 0 ? '+' : ''}${lift}% on ${controlIsToday(data) ? "today's price" : 'Control'}`
                  : undefined
              }
              variant={challenger}
              currency={data.currency}
              onChange={(price) => setPrice(1, price)}
            />
          </div>

          <div>
            <Button
              type="button"
              variant="ghost"
              color="gray"
              size="2"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
              style={{ minHeight: 44 }}
            >
              {moreOpen ? <ChevronUp16 /> : <ChevronDown16 />}
              More options
            </Button>
          </div>

          {moreOpen && (
            <VariantsEditor
              data={data}
              promosAllowed={promosAllowed}
              controlShownAbove={!basePlan}
              onChange={(updates) => {
                if (updates.name !== undefined) nameTouched.current = true;
                if (updates.variants !== undefined) pricesEdited.current = true;
                onChange(updates);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
